import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  WhatsApp as WhatsAppIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import {
  ASAAS_BILLING_TYPE_LABELS,
  ASAAS_CHARGE_STATUS_META,
  asaasApiError,
  asaasService,
  formatBRL,
  formatDateBR,
  receivableOpenBalance,
  useAsaasStatus,
  type AsaasBillingType,
  type AsaasCharge,
  type AsaasRepassePreview,
  type AsaasWhatsAppResult,
} from '../../services/asaasService';
import {
  PRESCRIPTION_INBOX_PROVIDER_LABEL,
  useClinicalDocumentWhatsAppInbox,
} from '../../utils/prescriptionWhatsAppInbox';

export interface AsaasChargeDialogPatient {
  id: string;
  name?: string | null;
  phone?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  patient: AsaasChargeDialogPatient | null;
  /** Conta a receber já escolhida (Financeiro do paciente / Contas a Receber). */
  receivable?: Record<string, any> | null;
  /** Inbox: lista as contas em aberto do paciente e permite cobrança avulsa. */
  allowPickReceivable?: boolean;
  /** Canal do inbox aberto — a cobrança sai pela mesma conversa. */
  defaultChannelId?: string | null;
  /** Inbox: permite escolher o profissional do repasse (split) nesta cobrança. */
  allowSelectSplitProfessional?: boolean;
  onChanged?: () => void;
}

const POLL_MS = 5000;
/** A cada N consultas ao banco, uma consulta direto na Asaas (cobre webhook atrasado). */
const ASAAS_REFRESH_EVERY = 6;

function todayPlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function newIdempotencyKey(): string {
  return `asaas-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const STANDALONE = '__standalone__';

const AsaasChargeDialog: React.FC<Props> = ({
  open,
  onClose,
  patient,
  receivable,
  allowPickReceivable,
  defaultChannelId,
  allowSelectSplitProfessional = false,
  onChanged,
}) => {
  const { isAdmin } = useAuth();
  const { status: asaasStatus, connected, loading: statusLoading } = useAsaasStatus(open);
  const account = asaasStatus?.account || null;
  const inbox = useClinicalDocumentWhatsAppInbox('asaas_charge', open);

  const [phase, setPhase] = useState<'loading' | 'form' | 'result'>('loading');
  const [charge, setCharge] = useState<AsaasCharge | null>(null);
  const [whatsappResult, setWhatsappResult] = useState<AsaasWhatsAppResult | null>(null);
  const [error, setError] = useState<{ message: string; code: string | null } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState<'pix' | 'link' | null>(null);

  const [openReceivables, setOpenReceivables] = useState<Record<string, any>[]>([]);
  const [openCharges, setOpenCharges] = useState<AsaasCharge[]>([]);
  const [target, setTarget] = useState<string>('');
  const [billingType, setBillingType] = useState<AsaasBillingType>('UNDEFINED');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [phone, setPhone] = useState('');
  const [channelId, setChannelId] = useState('');
  const [cpf, setCpf] = useState('');
  const [needsCpf, setNeedsCpf] = useState(false);
  const [repassePreview, setRepassePreview] = useState<AsaasRepassePreview | null>(null);
  const [splitProfessionalId, setSplitProfessionalId] = useState('');
  const [confirmBudgetExecution, setConfirmBudgetExecution] = useState(false);
  const [professionals, setProfessionals] = useState<Array<{ id: string; full_name: string }>>([]);
  const idempotencyRef = useRef(newIdempotencyKey());
  const pollCount = useRef(0);
  const notifiedPaid = useRef(false);

  const allowedTypes = useMemo<AsaasBillingType[]>(
    () => (account?.allowed_billing_types?.length ? account.allowed_billing_types : ['UNDEFINED', 'PIX']),
    [account],
  );

  const selectedReceivable = useMemo(() => {
    if (receivable) return receivable;
    return openReceivables.find((r) => r.id === target) || null;
  }, [receivable, openReceivables, target]);

  const isStandalone = !receivable && target === STANDALONE;

  const showCharge = useCallback(async (row: AsaasCharge) => {
    notifiedPaid.current = row.status === 'paid';
    pollCount.current = 0;
    setCharge(row);
    setPhase('result');
    if (row.pix_qr_base64 === undefined) {
      try {
        setCharge(await asaasService.getCharge(row.id));
      } catch {
        /* mantém a versão da lista */
      }
    }
  }, []);

  // Abertura: reaproveita cobrança em aberto da conta ou prepara o formulário.
  useEffect(() => {
    if (!open) return;
    setPhase('loading');
    setCharge(null);
    setWhatsappResult(null);
    setError(null);
    setNeedsCpf(false);
    setConfirmBudgetExecution(false);
    setCpf('');
    setDescription('');
    setAmount('');
    setSendWhatsApp(true);
    setPhone(patient?.phone || '');
    setChannelId(defaultChannelId || '');
    idempotencyRef.current = newIdempotencyKey();

    let cancelled = false;
    (async () => {
      try {
        if (receivable?.id) {
          const existing = await asaasService.listCharges({ transaction_ids: [receivable.id], status: 'open' });
          if (cancelled) return;
          if (existing[0]) {
            await showCharge(existing[0]);
            return;
          }
        } else if (allowPickReceivable && patient?.id) {
          const [recRes, charges] = await Promise.all([
            axios.get(getApiUrl('/financial-module/accounts-receivable'), {
              params: { patient_id: patient.id, limit: 100 },
            }),
            asaasService.listCharges({ patient_id: patient.id, status: 'open' }).catch(() => []),
          ]);
          if (cancelled) return;
          const rows: Record<string, any>[] = (recRes.data?.data || []).filter((r: any) => {
            const st = String(r.status || '').toLowerCase();
            return st !== 'paid' && st !== 'cancelled' && receivableOpenBalance(r) > 0;
          });
          setOpenReceivables(rows);
          setOpenCharges(charges);
          setTarget(rows[0]?.id || STANDALONE);
        }
        if (!cancelled) setPhase('form');
      } catch (e) {
        if (!cancelled) {
          setError(asaasApiError(e));
          setPhase('form');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, receivable, allowPickReceivable, patient?.id, patient?.phone, defaultChannelId, showCharge]);

  useEffect(() => {
    if (!account) return;
    setBillingType(account.default_billing_type && allowedTypes.includes(account.default_billing_type)
      ? account.default_billing_type
      : allowedTypes[0]);
    setDueDate(todayPlus(Number(account.due_days ?? 3)));
  }, [account, allowedTypes]);

  useEffect(() => {
    if (!defaultChannelId && inbox.channelId) setChannelId(inbox.channelId);
  }, [defaultChannelId, inbox.channelId]);

  const splitRepasseEnabled = !!account?.asaas_split_repasse_enabled;

  useEffect(() => {
    const loadPros =
      open
      && connected
      && splitRepasseEnabled
      && (
        allowSelectSplitProfessional
        || Boolean(selectedReceivable?.budget_id)
      );
    if (!loadPros) {
      setProfessionals([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/professionals'), { params: { is_active: true, limit: 200 } });
        const rows = (res.data?.data || res.data || []) as Array<{ id: string; full_name: string }>;
        if (!cancelled) setProfessionals(rows.filter((p) => p.id && p.full_name));
      } catch {
        if (!cancelled) setProfessionals([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, allowSelectSplitProfessional, splitRepasseEnabled, selectedReceivable?.budget_id, connected]);

  useEffect(() => {
    if (!open) return;
    const fromReceivable = selectedReceivable?.professional_id ? String(selectedReceivable.professional_id) : '';
    setSplitProfessionalId(fromReceivable);
    setConfirmBudgetExecution(false);
  }, [open, selectedReceivable?.id, selectedReceivable?.professional_id]);

  useEffect(() => {
    const txnId = selectedReceivable?.id;
    if (!open || phase !== 'form' || isStandalone || !txnId || !connected || !splitRepasseEnabled) {
      setRepassePreview(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const profId = splitProfessionalId || undefined;
        const data = await asaasService.previewRepasseSplit(String(txnId), profId, {
          assumeBudgetExecuted: confirmBudgetExecution,
        });
        if (!cancelled) setRepassePreview(data);
      } catch {
        if (!cancelled) setRepassePreview(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    phase,
    isStandalone,
    selectedReceivable?.id,
    connected,
    splitProfessionalId,
    confirmBudgetExecution,
    splitRepasseEnabled,
  ]);

  // Acompanha o pagamento em tempo real enquanto a cobrança está aberta.
  useEffect(() => {
    if (!open || phase !== 'result' || !charge) return undefined;
    if (charge.status !== 'pending' && charge.status !== 'overdue') return undefined;
    const timer = window.setInterval(async () => {
      pollCount.current += 1;
      try {
        const fresh = await asaasService.getCharge(charge.id, pollCount.current % ASAAS_REFRESH_EVERY === 0);
        setCharge((prev) => ({ ...fresh, pix_qr_base64: fresh.pix_qr_base64 ?? prev?.pix_qr_base64 }));
      } catch {
        /* tenta no próximo ciclo */
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, phase, charge]);

  useEffect(() => {
    if (charge?.status === 'paid' && !notifiedPaid.current) {
      notifiedPaid.current = true;
      onChanged?.();
    }
  }, [charge?.status, onChanged]);

  const handleSubmit = async () => {
    if (!patient?.id) return;
    setSubmitting(true);
    setError(null);
    setWhatsappResult(null);
    try {
      const budgetAck = splitRepasseEnabled && Boolean(repassePreview?.budget_repasse?.needs_execution_ack);
      const repasseProf =
        splitRepasseEnabled
        && splitProfessionalId
        && (allowSelectSplitProfessional || budgetAck)
          ? splitProfessionalId
          : undefined;
      const common = {
        billing_type: billingType,
        due_date: dueDate || undefined,
        send_whatsapp: sendWhatsApp,
        channel_id: sendWhatsApp && channelId ? channelId : undefined,
        phone: sendWhatsApp && phone.trim() ? phone.trim() : undefined,
        patient_cpf: needsCpf && cpf.trim() ? cpf.trim() : undefined,
        idempotency_key: idempotencyRef.current,
        repasse_professional_id: repasseProf,
        confirm_mark_budget_executed: budgetAck && confirmBudgetExecution ? true : undefined,
      };
      const result = isStandalone
        ? await asaasService.createStandaloneCharge({
            ...common,
            patient_id: patient.id,
            amount: Number(String(amount).replace(',', '.')),
            description: description.trim(),
          })
        : await asaasService.createCharge({
            ...common,
            transaction_id: String(selectedReceivable?.id || ''),
            description: description.trim() || undefined,
          });
      setWhatsappResult(result.whatsapp);
      await showCharge(result.charge);
      onChanged?.();
    } catch (e) {
      const err = asaasApiError(e);
      if (err.code === 'PATIENT_CPF_REQUIRED') setNeedsCpf(true);
      // Erro de validação: a próxima tentativa é outra operação.
      idempotencyRef.current = newIdempotencyKey();
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!charge) return;
    setSending(true);
    setWhatsappResult(null);
    try {
      const result = await asaasService.sendWhatsApp(charge.id, {
        channel_id: channelId || undefined,
        phone: phone.trim() || undefined,
      });
      setWhatsappResult(result);
      setCharge(await asaasService.getCharge(charge.id));
    } catch (e) {
      const err = asaasApiError(e);
      setWhatsappResult({ sent: false, error: err.message, code: err.code });
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async () => {
    if (!charge) return;
    if (!window.confirm('Cancelar esta cobrança? O link e o QR Code deixam de aceitar pagamento.')) return;
    setCancelling(true);
    try {
      setCharge(await asaasService.cancelCharge(charge.id));
      onChanged?.();
    } catch (e) {
      setError(asaasApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  const copy = async (kind: 'pix' | 'link', text: string | null) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      /* navegador sem permissão de área de transferência */
    }
  };

  const openBalance = selectedReceivable ? receivableOpenBalance(selectedReceivable) : 0;
  const splitProfRequired =
    splitRepasseEnabled
    && allowSelectSplitProfessional
    && (isStandalone || !!selectedReceivable);

  const budgetAckRequired = splitRepasseEnabled && Boolean(repassePreview?.budget_repasse?.needs_execution_ack);

  const canSubmit =
    !!patient?.id &&
    !submitting &&
    connected &&
    (isStandalone
      ? Number(String(amount).replace(',', '.')) >= 5 && description.trim().length > 0
      : !!selectedReceivable && openBalance >= 5) &&
    (!needsCpf || cpf.replace(/\D/g, '').length >= 11) &&
    (!splitProfRequired || !!splitProfessionalId) &&
    (!budgetAckRequired || (!!splitProfessionalId && confirmBudgetExecution));

  const qrSrc = charge?.pix_qr_base64
    ? `data:image/png;base64,${charge.pix_qr_base64}`
    : charge?.pix_qr_image_url || null;
  const statusMeta = charge ? ASAAS_CHARGE_STATUS_META[charge.status] : null;
  const isOpenCharge = charge?.status === 'pending' || charge?.status === 'overdue';

  const whatsappFields = (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        label="WhatsApp do paciente"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="(11) 99999-9999"
        fullWidth
      />
      {inbox.inboxes.length > 0 && (
        <FormControl size="small" fullWidth>
          <InputLabel>Enviar pelo inbox</InputLabel>
          <Select label="Enviar pelo inbox" value={channelId} onChange={(e) => setChannelId(String(e.target.value))}>
            <MenuItem value="">Automático (WhatsApp padrão da unidade)</MenuItem>
            {inbox.inboxes.map((ib) => (
              <MenuItem key={ib.channelId} value={ib.channelId}>
                {ib.name} · {PRESCRIPTION_INBOX_PROVIDER_LABEL[ib.provider]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        Cobrar via Asaas
        {patient?.name && (
          <Typography component="span" variant="body2" color="text.secondary" noWrap>
            · {patient.name}
          </Typography>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {(phase === 'loading' || statusLoading) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {phase !== 'loading' && !statusLoading && !connected && phase === 'form' && (
          <Alert severity="info">
            {asaasStatus && !asaasStatus.installed
              ? 'A integração Asaas ainda não foi instalada no banco desta clínica. Fale com o suporte Klinvia.'
              : 'A Asaas ainda não está conectada nesta unidade.'}
            {isAdmin && asaasStatus?.installed !== false && (
              <>
                {' '}
                <RouterLink to="/clinic/settings">Conectar em Configurações</RouterLink>.
              </>
            )}
          </Alert>
        )}

        {phase === 'form' && connected && (
          <Stack spacing={2}>
            {allowPickReceivable && !receivable && (
              <>
                {openCharges.length > 0 && (
                  <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Cobranças em aberto</Typography>
                    <Stack spacing={0.5}>
                      {openCharges.map((c) => (
                        <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                            {c.description || 'Cobrança'} · {formatBRL(c.value)} · vence {formatDateBR(c.due_date)}
                          </Typography>
                          <Button size="small" onClick={() => void showCharge(c)}>Abrir</Button>
                        </Box>
                      ))}
                    </Stack>
                  </Alert>
                )}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>O que cobrar</Typography>
                  <RadioGroup value={target} onChange={(e) => setTarget(e.target.value)}>
                    {openReceivables.map((r) => (
                      <FormControlLabel
                        key={r.id}
                        value={r.id}
                        control={<Radio size="small" />}
                        label={
                          <Typography variant="body2">
                            {r.description || 'Conta a receber'} · <strong>{formatBRL(receivableOpenBalance(r))}</strong>
                            {r.due_date ? ` · vence ${formatDateBR(r.due_date)}` : ''}
                          </Typography>
                        }
                      />
                    ))}
                    <FormControlLabel
                      value={STANDALONE}
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Nova cobrança avulsa</Typography>}
                    />
                  </RadioGroup>
                </Box>
              </>
            )}

            {receivable && (
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary">Conta a receber</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{receivable.description || 'Conta a receber'}</Typography>
                <Typography variant="body2">
                  Saldo em aberto: <strong>{formatBRL(openBalance)}</strong>
                </Typography>
                {openBalance < 5 && (
                  <Typography variant="caption" color="error">A Asaas só aceita cobranças a partir de R$ 5,00.</Typography>
                )}
              </Box>
            )}

            {splitRepasseEnabled && (budgetAckRequired || allowSelectSplitProfessional) && (
              <FormControl size="small" fullWidth required={splitProfRequired || budgetAckRequired}>
                <InputLabel>
                  {budgetAckRequired ? 'Profissional executor (repasse)' : 'Profissional do repasse (split)'}
                </InputLabel>
                <Select
                  label={budgetAckRequired ? 'Profissional executor (repasse)' : 'Profissional do repasse (split)'}
                  value={splitProfessionalId}
                  onChange={(e) => setSplitProfessionalId(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>Selecione…</em>
                  </MenuItem>
                  {professionals.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>
                  ))}
                </Select>
                {!budgetAckRequired && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Usa as Regras de Repasse. Wallet Asaas vinculada no cadastro do profissional para split automático.
                  </Typography>
                )}
              </FormControl>
            )}

            {budgetAckRequired && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Orçamento {repassePreview?.budget_repasse?.budget_number || ''} — procedimento ainda não executado
                </Typography>
                <Typography variant="body2" component="div">
                  Para liberar repasse sobre este recebimento, o(s) item(ns) abaixo será(ão) marcado(s) como{' '}
                  <strong>executado(s)</strong> ao gerar a cobrança (mesmo fluxo do painel clínico, incluindo estoque se houver):
                </Typography>
                <Box component="ul" sx={{ m: '8px 0 0', pl: 2.5 }}>
                  {(repassePreview?.budget_repasse?.pending_items || []).map((it) => (
                    <li key={it.id}>
                      <Typography variant="body2">{it.procedure_name || 'Procedimento'}</Typography>
                    </li>
                  ))}
                </Box>
                <FormControlLabel
                  sx={{ mt: 1.5, alignItems: 'flex-start' }}
                  control={(
                    <Checkbox
                      checked={confirmBudgetExecution}
                      onChange={(e) => setConfirmBudgetExecution(e.target.checked)}
                    />
                  )}
                  label={
                    <Typography variant="body2">
                      Confirmo que o procedimento será registrado como executado pelo profissional selecionado
                      e reconheço que isso libera o comissionamento conforme as Regras de Repasse.
                    </Typography>
                  }
                />
              </Alert>
            )}

            {repassePreview?.split_enabled && repassePreview.snapshot && !isStandalone && (
              (() => {
                const s = repassePreview.snapshot;
                if (s.skipped_reason === 'procedimento_repasse_na_execucao' && !budgetAckRequired) {
                  return (
                    <Alert severity="info">
                      Repasse automático (split) não se aplica a procedimentos/cirurgias — use Repasses após executar no painel.
                    </Alert>
                  );
                }
                if (s.skipped_reason === 'wallet_profissional_ausente') {
                  return (
                    <Alert severity="warning">
                      Split de repasse ativo, mas o profissional da conta não tem wallet Asaas vinculada. Cadastre em Profissionais antes de gerar a cobrança.
                    </Alert>
                  );
                }
                if (s.skipped_reason === 'conta_sem_profissional') {
                  return (
                    <Alert severity="warning">
                      Split de repasse ativo: vincule um profissional à conta a receber (ou escolha uma consulta com agenda do dia).
                    </Alert>
                  );
                }
                if (s.expected_commission > 0 && s.professional_name) {
                  const detail = s.calc_type === 'fixed'
                    ? formatBRL(s.fixed_value || s.expected_commission)
                    : `${s.percentage ?? 0}% sobre o líquido na recepção`;
                  return (
                    <Alert severity="info">
                      Repasse automático (Regras de Repasse): <strong>{s.professional_name}</strong> — {detail}
                      {' '}(estimativa interna {formatBRL(s.expected_commission)} sobre base {formatBRL(s.gross_base)}).
                    </Alert>
                  );
                }
                return null;
              })()
            )}

            {isStandalone && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  label="Descrição"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ex.: Consulta de avaliação"
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Valor"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  sx={{ minWidth: 150 }}
                />
              </Stack>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>Forma de pagamento</InputLabel>
                <Select
                  label="Forma de pagamento"
                  value={billingType}
                  onChange={(e) => setBillingType(e.target.value as AsaasBillingType)}
                >
                  {allowedTypes.map((t) => (
                    <MenuItem key={t} value={t}>{ASAAS_BILLING_TYPE_LABELS[t]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="Vencimento"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayPlus(0) }}
                sx={{ minWidth: 170 }}
              />
            </Stack>

            {needsCpf && (
              <TextField
                size="small"
                label="CPF do paciente"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                helperText="A Asaas exige CPF do pagador. Ele será salvo no cadastro do paciente."
                autoFocus
              />
            )}

            <Divider />
            <FormControlLabel
              control={<Switch checked={sendWhatsApp} onChange={(e) => setSendWhatsApp(e.target.checked)} />}
              label="Enviar ao paciente pelo WhatsApp (link, QR Code e PIX copia e cola)"
            />
            {sendWhatsApp && whatsappFields}

            {error && <Alert severity="error">{error.message}</Alert>}
          </Stack>
        )}

        {phase === 'result' && charge && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {statusMeta && <Chip label={statusMeta.label} color={statusMeta.color} size="small" />}
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatBRL(charge.value)}</Typography>
              <Typography variant="body2" color="text.secondary">
                · vence {formatDateBR(charge.due_date)} · {charge.billing_type_label}
              </Typography>
              {isOpenCharge && (
                <Tooltip title="Consultar a Asaas agora">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        setCharge(await asaasService.getCharge(charge.id, true));
                      } catch {
                        /* ignora */
                      }
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {charge.description && (
              <Typography variant="body2" color="text.secondary">{charge.description}</Typography>
            )}

            {charge.status === 'paid' && (
              <Alert icon={<CheckCircleIcon />} severity="success">
                Pagamento recebido{charge.paid_billing_type ? ` (${ASAAS_BILLING_TYPE_LABELS[charge.paid_billing_type as AsaasBillingType] || charge.paid_billing_type})` : ''}.
                {' '}A conta a receber foi baixada automaticamente como recebida.
              </Alert>
            )}
            {charge.settlement_error && <Alert severity="warning">{charge.settlement_error}</Alert>}

            {isOpenCharge && (
              <>
                {qrSrc && charge.pix_payload && (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box
                      component="img"
                      src={qrSrc}
                      alt="QR Code PIX"
                      sx={{ width: 168, height: 168, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}
                    />
                    <Box sx={{ flex: 1, width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>PIX copia e cola</Typography>
                      <TextField
                        size="small"
                        value={charge.pix_payload}
                        fullWidth
                        multiline
                        maxRows={3}
                        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                      />
                      <Button
                        size="small"
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={() => void copy('pix', charge.pix_payload)}
                        sx={{ mt: 0.5 }}
                      >
                        {copied === 'pix' ? 'Copiado!' : 'Copiar código PIX'}
                      </Button>
                    </Box>
                  </Box>
                )}
                {charge.invoice_url && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Link de pagamento</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField size="small" value={charge.invoice_url} fullWidth InputProps={{ readOnly: true }} />
                      <Tooltip title={copied === 'link' ? 'Copiado!' : 'Copiar link'}>
                        <IconButton onClick={() => void copy('link', charge.invoice_url)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Abrir fatura">
                        <IconButton component="a" href={charge.invoice_url} target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                )}

                <Divider />
                {whatsappResult?.sent && (
                  <Alert severity="success">
                    Cobrança enviada no WhatsApp{whatsappResult.phone ? ` (${whatsappResult.phone})` : ''}
                    {whatsappResult.via_template ? ' pelo template aprovado da API oficial' : ''}. A mensagem aparece no chat do paciente.
                  </Alert>
                )}
                {whatsappResult && !whatsappResult.sent && (
                  <Alert severity="warning">
                    Cobrança criada, mas não foi enviada no WhatsApp: {whatsappResult.error}
                    {' '}Você pode copiar o link acima ou tentar de novo.
                  </Alert>
                )}
                {!whatsappResult && charge.sent_whatsapp_at && (
                  <Typography variant="body2" color="text.secondary">
                    Enviada no WhatsApp em {new Date(charge.sent_whatsapp_at).toLocaleString('pt-BR')}.
                  </Typography>
                )}
                {whatsappFields}
                <Typography variant="caption" color="text.secondary">
                  Esta tela atualiza sozinha: quando o paciente pagar, o status muda para Pago e a conta é baixada.
                </Typography>
              </>
            )}
            {error && <Alert severity="error">{error.message}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {phase === 'result' && charge && isOpenCharge && (
          <Button color="error" onClick={() => void handleCancel()} disabled={cancelling} sx={{ mr: 'auto' }}>
            {cancelling ? 'Cancelando…' : 'Cancelar cobrança'}
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
        {phase === 'form' && connected && (
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {submitting ? 'Gerando…' : sendWhatsApp ? 'Gerar e enviar' : 'Gerar cobrança'}
          </Button>
        )}
        {phase === 'result' && charge && isOpenCharge && (
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={() => void handleSendWhatsApp()}
            disabled={sending}
          >
            {sending ? 'Enviando…' : charge.sent_whatsapp_at ? 'Reenviar no WhatsApp' : 'Enviar no WhatsApp'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AsaasChargeDialog;
